import dayjs from '@/components/NewUI/utils/dayjs';

import { canadianTimeZoneRule } from './canadianTimeZones';

import type { Dayjs } from 'dayjs';

const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * MINUTE_MS;
// react-big-calendar's dayjs localizer builds this string when it merges a date with a time.
const CALENDAR_MERGE_FORMAT = 'MM/DD/YYYY HH:mm:ss';

type WallClockField = 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second';

const wallClockFormats = new Map<string, Intl.DateTimeFormat>();

const wallClockFormat = (timeZone: string) => {
  let format = wallClockFormats.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      hour: 'numeric',
      hour12: false,
      minute: 'numeric',
      month: 'numeric',
      second: 'numeric',
      timeZone,
      year: 'numeric'
    });
    wallClockFormats.set(timeZone, format);
  }
  return format;
};

export const isKnownTimeZone = (timeZone?: string | null): timeZone is string => {
  if (!timeZone) return false;
  try {
    wallClockFormat(timeZone);
    return true;
  } catch {
    return false;
  }
};

export const parseCalendarDate = (date: dayjs.ConfigType): Dayjs => {
  if (typeof date === 'string') {
    const datePart = /^(\d{4}-\d{2}-\d{2})(?:[T ]|$)/.exec(date)?.[1];
    if (datePart && !dayjs.utc(datePart, 'YYYY-MM-DD', true).isValid()) {
      return dayjs(Number.NaN);
    }
  }
  return dayjs(date);
};

// The wall clock in `timeZone` at `date`, as a UTC-mode dayjs whose fields are that wall clock.
// Avoid dayjs's browser DST conversion by reading Intl fields or applying the Canadian rule.
export const toZonedWallClock = (date: dayjs.ConfigType, timeZone: string): Dayjs => {
  const instant = parseCalendarDate(date);
  // Intl throws on an invalid date; stay invalid like moment did.
  if (!instant.isValid()) return dayjs.utc(Number.NaN);
  const rule = canadianTimeZoneRule(timeZone, instant.valueOf());
  if (rule) return instant.utc().add(rule.offsetMinutes, 'minute');
  const parts = (
    wallClockFormat(timeZone) as Intl.DateTimeFormat & {
      formatToParts: (value: Date) => { type: string; value: string }[];
    }
  ).formatToParts(instant.toDate());
  const field = (type: WallClockField) =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);

  return dayjs.utc(
    Date.UTC(
      field('year'),
      field('month') - 1,
      field('day'),
      field('hour') % 24,
      field('minute'),
      field('second'),
      instant.millisecond()
    )
  );
};

const zoneOffsetMinutes = (timestamp: number, timeZone: string) =>
  (toZonedWallClock(timestamp, timeZone).valueOf() - timestamp) / MINUTE_MS;

// The instant at which `timeZone` shows `wallClock` (a UTC-mode dayjs from toZonedWallClock).
// Like moment-timezone, a repeated wall time resolves to its first occurrence and a skipped one
// moves forward by the gap.
export const fromZonedWallClock = (wallClock: Dayjs, timeZone: string): Date => {
  if (!wallClock.isValid()) return new Date(Number.NaN);
  const wallTime = wallClock.valueOf();
  const offsetBefore = zoneOffsetMinutes(wallTime - DAY_MS, timeZone);
  const offsetAfter = zoneOffsetMinutes(wallTime + DAY_MS, timeZone);
  const matches = [offsetBefore, offsetAfter]
    .map((offset) => ({ instant: wallTime - offset * MINUTE_MS, offset }))
    .filter(({ instant, offset }) => zoneOffsetMinutes(instant, timeZone) === offset)
    .map(({ instant }) => instant);

  return new Date(matches.length ? Math.min(...matches) : wallTime - offsetBefore * MINUTE_MS);
};

// The dayjs that react-big-calendar's dayjs localizer works in: UTC mode, so its arithmetic
// runs on the user's wall clock with no DST jumps. Dates given to the calendar go through
// toZonedWallClock and dates it hands back through fromZonedWallClock.
export const wallClockDayjs = Object.assign(
  (date?: dayjs.ConfigType) =>
    typeof date === 'string' ? dayjs.utc(date, CALENDAR_MERGE_FORMAT) : dayjs.utc(date),
  { extend: dayjs.extend, localeData: dayjs.localeData, max: dayjs.max, min: dayjs.min }
);

interface WallClockLocalizer {
  getDstOffset: (start: Date, end: Date) => number;
  getTimezoneOffset: (date: Date) => number;
  segmentOffset: number;
}

// Finish a react-big-calendar dayjs localizer built on wallClockDayjs. The wall clock has no
// DST, so the calendar must not read the browser's offsets from its dates. Multi-day events
// keep the one-day trim the moment localizer applied when the user's zone was ahead of the
// browser's.
export const asWallClockLocalizer = <T extends WallClockLocalizer>(
  localizer: T,
  timeZone: string
): T => {
  const now = Date.now();
  return Object.assign(localizer, {
    getDstOffset: () => 0,
    getTimezoneOffset: () => 0,
    segmentOffset: zoneOffsetMinutes(now, timeZone) > -new Date(now).getTimezoneOffset() ? 1 : 0
  });
};
